import {
  type AreaData,
  type AreaId,
  type AreaListSuccess,
  isDefined,
  isEmpty,
  isNotDefined,
  isNotEmpty,
} from '@terros/common'
import { type InfiniteData } from '@tanstack/react-query'
import { type AreaListMatch } from './listMatch'

type AreaPages = InfiniteData<AreaListSuccess>

/**
 * Applies one area change to cached v2 list pages, so the list is right before Elastic indexes the write.
 * - Already listed: replaced in place, or dropped (and `total` decremented) when `after` clearly no longer matches.
 * - Not listed: put at the top of the first page (and `total` incremented) only when `after` clearly matches and
 *   every page is loaded. A later page could return it again, so partly loaded lists wait for the refetch.
 */
type AreaPagesChange = {
  data: AreaPages
  areaId: AreaId
  /** The area after the write, or undefined when it was removed. */
  after?: AreaData
  match: AreaListMatch
}

export function applyAreaToPages({ data, areaId, after, match }: AreaPagesChange): AreaPages {
  const listed = data.pages.some((page) => page.areas.some((a) => a.areaId === areaId))
  if (listed) {
    if (isDefined(after) && match !== false) return mapAreas(data, (areas) => replaceArea(areas, after))
    return withTotalDelta(
      mapAreas(data, (areas) => areas.filter((a) => a.areaId !== areaId)),
      -1
    )
  }
  if (isDefined(after) && match === true && isNotEmpty(data.pages) && isFullyLoaded(data)) {
    const [first, ...rest] = data.pages
    return withTotalDelta({ ...data, pages: [{ ...first, areas: [after, ...first.areas] }, ...rest] }, 1)
  }
  return data
}

/** Mirrors getNextInfinitePageInput: no cursor left, an empty last page, or every area in `total` loaded. */
function isFullyLoaded({ pages }: AreaPages): boolean {
  const last = pages.at(-1)
  if (isNotDefined(last) || isEmpty(last.areas) || isNotDefined(last.sortTimestamp)) return true
  const loaded = pages.reduce((sum, page) => sum + page.areas.length, 0)
  return isDefined(last.total) && loaded >= last.total
}

/** Merges so list-only fields the write response lacks, like `accountCounts`, survive. */
function replaceArea(areas: AreaData[], area: AreaData): AreaData[] {
  return areas.map((a) => (a.areaId === area.areaId ? { ...a, ...area } : a))
}

function mapAreas(data: AreaPages, fn: (areas: AreaData[]) => AreaData[]): AreaPages {
  return { ...data, pages: data.pages.map((page) => ({ ...page, areas: fn(page.areas) })) }
}

function withTotalDelta(data: AreaPages, delta: number): AreaPages {
  return {
    ...data,
    pages: data.pages.map((page) =>
      isDefined(page.total) ? { ...page, total: Math.max(page.total + delta, 0) } : page
    ),
  }
}

/** Change to a cached area count: +1 or -1 only when both sides are known, else 0 and the delayed refetch fixes it. */
export function areaCountDelta(before: AreaListMatch, after: AreaListMatch): number {
  if (isDefined(before) && isDefined(after)) return Number(after) - Number(before)
  return 0
}
